import type { Role } from '@/data/types'
import type { AgentMfaStatus } from './agentTypes'

/** Demo console MFA for agent ladder rows (Keycloak-shaped, not Player-API). */
export const DEMO_AGENT_MFA: AgentMfaStatus[] = [
  {
    username: 'company_master',
    accountId: '11111111-1111-4111-8111-111111111111',
    role: 'company-master',
    displayName: 'Company Master',
    found: true,
    mfaEnabled: true,
    methods: ['totp', 'passkey'],
    keycloakUsername: 'company_master@genesis.local',
  },
  {
    username: 'triple_master',
    accountId: '22222222-2222-4222-8222-222222222222',
    role: 'triple-master',
    displayName: 'Triple Master',
    found: true,
    mfaEnabled: true,
    methods: ['totp'],
    keycloakUsername: 'triple_master@genesis.local',
  },
  {
    username: 'double_master',
    accountId: '33333333-3333-4333-8333-333333333333',
    role: 'double-master',
    displayName: 'Double Master',
    found: true,
    mfaEnabled: false,
    methods: [],
    keycloakUsername: 'double_master@genesis.local',
  },
  {
    username: 'super_master',
    accountId: '44444444-4444-4444-8444-444444444444',
    role: 'super-master',
    displayName: 'Super Master',
    found: true,
    mfaEnabled: true,
    methods: ['passkey'],
    keycloakUsername: 'super_master@genesis.local',
  },
  {
    username: 'master_one',
    accountId: '55555555-5555-4555-8555-555555555555',
    role: 'master',
    displayName: 'Master One',
    found: true,
    mfaEnabled: false,
    methods: [],
    keycloakUsername: 'master_one@genesis.local',
  },
  {
    username: 'master_two',
    role: 'master',
    displayName: 'Master Two',
    found: true,
    mfaEnabled: true,
    methods: ['totp', 'recovery'],
    keycloakUsername: 'master_two@genesis.local',
  },
]

export function filterDemoAgents(query: {
  q?: string
  mfa?: 'all' | 'on' | 'off'
  roles?: Role[]
}): AgentMfaStatus[] {
  const q = (query.q || '').trim().toLowerCase()
  const mfa = query.mfa || 'all'
  let rows = DEMO_AGENT_MFA.slice()
  if (q) {
    rows = rows.filter(
      (u) =>
        u.username.toLowerCase().includes(q) ||
        (u.displayName || '').toLowerCase().includes(q) ||
        (u.keycloakUsername || '').toLowerCase().includes(q),
    )
  }
  if (mfa === 'on') rows = rows.filter((u) => u.mfaEnabled)
  if (mfa === 'off') rows = rows.filter((u) => !u.mfaEnabled)
  if (query.roles?.length) {
    const set = new Set(query.roles)
    rows = rows.filter((u) => u.role && set.has(u.role))
  }
  return rows
}

export function findDemoAgent(usernameOrId: string): AgentMfaStatus | null {
  const key = usernameOrId.trim().toLowerCase()
  return (
    DEMO_AGENT_MFA.find(
      (u) =>
        u.username.toLowerCase() === key ||
        (u.accountId && u.accountId.toLowerCase() === key) ||
        (u.keycloakUsername && u.keycloakUsername.toLowerCase() === key),
    ) ?? null
  )
}

export function demoAgentSummary(rows: AgentMfaStatus[] = DEMO_AGENT_MFA) {
  const total = rows.length
  const mfaEnabled = rows.filter((r) => r.mfaEnabled).length
  const totp = rows.filter((r) => r.methods.includes('totp')).length
  const passkey = rows.filter((r) => r.methods.includes('passkey')).length
  return {
    totalAgents: total,
    mfaEnabled,
    mfaDisabled: total - mfaEnabled,
    totpEnabled: totp,
    passkeyEnabled: passkey,
    adoptionRate: total > 0 ? mfaEnabled / total : 0,
  }
}
